import { z } from 'zod';

/**
 * Phase ⑥ StrategistAgent — décision per-argument + structuration adversariale.
 *
 * Architecture : N instances en parallèle, 1 par argument. Chaque instance
 * arbitre seule. Un agrégateur déterministe (pas de LLM) collecte les sorties,
 * filtre `decision='retenu'`, ordonne par `force` et prépare le bundle pour ⑦.
 *
 * **Hard fail si 0 retenu** (D29 + D30) — pas d'abstention globale.
 *
 * Voir hub doc § 4 ⑥ (`docs/architecture/plan-detaille-v2.md`).
 */

export const ArgumentForceSchema = z.enum([
  'principal',
  'subsidiaire',
  'tres_subsidiaire',
]);
export type ArgumentForce = z.infer<typeof ArgumentForceSchema>;

export const JpAppuiRoleSchema = z.enum(['principe', 'illustration']);
export type JpAppuiRole = z.infer<typeof JpAppuiRoleSchema>;

export const ArticleCitationSchema = z.object({
  code: z.string(),
  article: z.string(),
  citation_exacte: z.string(),
}).strict();
export type ArticleCitation = z.infer<typeof ArticleCitationSchema>;

export const JpAppuiSchema = z.object({
  ecli: z.string().optional(),
  pourvoi: z.string().optional(),
  citation_exacte: z.string(),
  role: JpAppuiRoleSchema,
}).strict();
export type JpAppui = z.infer<typeof JpAppuiSchema>;

export const JpAdverseSchema = z.object({
  ecli: z.string().optional(),
  pourvoi: z.string().optional(),
  attendu: z.string(),
}).strict();
export type JpAdverse = z.infer<typeof JpAdverseSchema>;

export const ContreArgumentSchema = z.object({
  contre_argument: z.string(),
  jp_adverse: JpAdverseSchema.optional(),
  parade: z.string(),
}).strict();
export type ContreArgument = z.infer<typeof ContreArgumentSchema>;

export const ArgumentRedigeSchema = z.object({
  titre_final: z.string(),
  force: ArgumentForceSchema,

  en_droit: z.object({
    majeure: z.string(),                           // principe posé (éventuellement refondé — D45)
    articles_cites: z.array(ArticleCitationSchema),
    jp_appui: z.array(JpAppuiSchema),
  }).strict(),

  application_aux_faits: z.object({
    mineure: z.string(),                           // syllogisme : faits du dossier
  }).strict(),

  conclusion_partielle: z.string(),

  // Adversarial — ce qui justifie le top-tier model en ⑥
  contre_arguments_anticipes: z.array(ContreArgumentSchema),
  vulnerabilites_residuelles: z.array(z.string()),
}).strict();
export type ArgumentRedige = z.infer<typeof ArgumentRedigeSchema>;

/**
 * Output d'UNE instance Strategist (1 argument).
 * Si `decision='retenu'`, `argument_redige` est obligatoire.
 * Si `decision='ecarte'`, il est absent.
 */
export const StrategistDecisionSchema = z.discriminatedUnion('decision', [
  z.object({
    arg_id: z.string(),
    decision: z.literal('retenu'),
    raison_decision: z.string(),
    argument_redige: ArgumentRedigeSchema,
  }).strict(),
  z.object({
    arg_id: z.string(),
    decision: z.literal('ecarte'),
    raison_decision: z.string(),
  }).strict(),
]);
export type StrategistDecision = z.infer<typeof StrategistDecisionSchema>;

/**
 * Bundle post-agrégation — input de ⑦ FinalPlanWriter.
 * Produit par l'agrégateur déterministe après collecte des N décisions.
 */
export const StrategistBundleSchema = z.object({
  arguments_retenus: z.array(
    z.object({
      arg_id: z.string(),
    }).merge(ArgumentRedigeSchema).strict(),
  ).min(1),                                         // hard fail si 0 (STRATEGIST_NO_ARGUMENT_RETAINED)

  arguments_ecartes: z.array(
    z.object({
      arg_id: z.string(),
      titre: z.string(),
      raison: z.string(),
    }).strict(),
  ),

  meta: z.object({
    risques_strategiques: z.array(z.string()),
    confidence_globale: z.number().min(0).max(1),
  }).strict(),
}).strict();
export type StrategistBundle = z.infer<typeof StrategistBundleSchema>;
